import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { ADHD_ROUTINES } from '../data/adhdRoutines';

const useDisciplineStore = create(
  persist(
    (set, get) => ({
      // Existing state
      goals: [],
      timers: [],
      xp: 0,
      completedGoals: [],
      streak: 0,

      // New state for enhanced stats
      categoryStats: {
        productivity: { accepted: 0, completed: 0, cancelled: 0 },
        health: { accepted: 0, completed: 0, cancelled: 0 },
        wellness: { accepted: 0, completed: 0, cancelled: 0 },
        learning: { accepted: 0, completed: 0, cancelled: 0 }
      },
      dailyStats: {},
      dailyTasks: {}, // Indexed by "YYYY-MM-DD"
      routines: [],   // Array of task templates

      // Actions
      addGoal: (goal) => set((state) => ({
        goals: [...state.goals, { ...goal, id: Date.now().toString() }]
      })),

      // Daily Task Actions
      addTask: (dateStr, task) => set((state) => {
        const currentTasks = state.dailyTasks[dateStr] || [];
        // Prevent duplicates if needed, or just append
        return {
          dailyTasks: {
            ...state.dailyTasks,
            [dateStr]: [...currentTasks, { ...task, id: Date.now().toString() + Math.random(), completed: false }]
          }
        };
      }),

      toggleTask: (dateStr, taskId) => set((state) => {
        const tasks = state.dailyTasks[dateStr] || [];
        const taskIndex = tasks.findIndex(t => t.id === taskId);

        if (taskIndex === -1) return state;

        const updatedTasks = [...tasks];
        const task = updatedTasks[taskIndex];
        const newCompleted = !task.completed;

        updatedTasks[taskIndex] = { ...task, completed: newCompleted };

        // Handle Statistics Update (XP & Streak)
        // If completing (and wasn't complete before)
        let xpChange = 0;
        let newStreak = state.streak;
        let newDailyStats = { ...state.dailyStats };
        let newXP = state.xp;
        let newLastActive = state.lastActiveDate;

        if (newCompleted) {
          const xpAmount = task.subtasks ? 20 : 15; // Simple logic: 20 if complex, 15 if simple
          xpChange = xpAmount;

          // Re-use completeTask logic sort of
          const today = new Date().toDateString();

          // Only update streak if doing tasks for TODAY
          if (new Date(dateStr).toDateString() === today) {
            if (state.lastActiveDate !== today) {
              const yesterday = new Date();
              yesterday.setDate(yesterday.getDate() - 1);
              if (state.lastActiveDate === yesterday.toDateString()) {
                newStreak += 1;
              } else {
                newStreak = 1;
              }
              newLastActive = today;
            }

            // Update Daily Stats
            if (!newDailyStats[today]) {
              newDailyStats[today] = { completed: 0, cancelled: 0, xpEarned: 0 };
            }
            newDailyStats[today].completed += 1;
            newDailyStats[today].xpEarned += xpAmount;
          }
          newXP += xpAmount;
        }
        // Note: We don't deduct XP if unchecking, to avoid punishment for misclicks, 
        // but normally strictly one might. We'll leave it additive for now for positivity.

        return {
          dailyTasks: { ...state.dailyTasks, [dateStr]: updatedTasks },
          xp: newXP,
          streak: newStreak,
          lastActiveDate: newLastActive,
          dailyStats: newDailyStats
        };
      }),

      addRoutine: (routine) => set((state) => ({
        routines: [...state.routines, { ...routine, id: Date.now().toString() }]
      })),

      generateDailyTasks: (dateStr) => set((state) => {
        // If tasks already exist for this date, do nothing
        if (state.dailyTasks[dateStr] && state.dailyTasks[dateStr].length > 0) return state;

        // Determine source: user routines OR default ADHD Standard routine
        let sourceTasks = state.routines;

        if (state.routines.length === 0) {
          // Use Standard Day as default auto-pilot
          sourceTasks = ADHD_ROUTINES.standard.tasks;
        }

        const newTasks = sourceTasks.map(r => ({
          ...r,
          id: Date.now().toString() + Math.random(), // New ID for instance
          completed: false,
          isRoutine: true,
          // Ensure icons are strings/objects for UI not to break (simple normalization)
          icon: r.activeIcon || r.icon || '📝'
        }));

        return {
          dailyTasks: { ...state.dailyTasks, [dateStr]: newTasks }
        };
      }),

      // Force Regenerate Day with specific Routine ID
      applyAutoPilot: (dateStr, routineId) => set((state) => {
        const template = ADHD_ROUTINES[routineId]?.tasks || ADHD_ROUTINES.standard.tasks;

        const newTasks = template.map(r => ({
          ...r,
          id: Date.now().toString() + Math.random(),
          completed: false,
          isRoutine: true,
          icon: r.activeIcon || r.icon || '📝'
        }));

        // Overwrite tasks for the day (or append? Overwrite seems safer for "Reset")
        return {
          dailyTasks: { ...state.dailyTasks, [dateStr]: newTasks }
        };
      }),

      startTimer: (goalId) => {
        const { goals } = get();
        const goal = goals.find(g => g.id === goalId);

        if (goal) {
          const timer = {
            id: Date.now().toString(),
            goalId: goal.id,
            goalName: goal.name,
            category: goal.category,
            duration: goal.duration,
            xpReward: goal.xpReward,
            startTime: new Date().toISOString(),
            endTime: null,
            isCompleted: false,
            isCancelled: false,
            isActive: true
          };

          set((state) => ({
            timers: [...state.timers, timer]
          }));

          return timer.id;
        }
      },

      completeTimer: (timerId) => set((state) => {
        const timer = state.timers.find(t => t.id === timerId);
        if (!timer) return state;

        const updatedTimers = state.timers.map(t =>
          t.id === timerId
            ? { ...t, isCompleted: true, isActive: false, endTime: new Date().toISOString() }
            : t
        );

        const newXP = state.xp + (timer.xpReward || 0);

        // Update category stats
        const newCategoryStats = { ...state.categoryStats };
        if (timer.category && newCategoryStats[timer.category]) {
          newCategoryStats[timer.category].completed += 1;
        }

        // Update daily stats
        const today = new Date().toDateString();
        const newDailyStats = { ...state.dailyStats };
        if (!newDailyStats[today]) {
          newDailyStats[today] = { completed: 0, cancelled: 0, xpEarned: 0 };
        }
        newDailyStats[today].completed += 1;
        newDailyStats[today].xpEarned += (timer.xpReward || 0);

        return {
          timers: updatedTimers,
          xp: newXP,
          completedGoals: [...state.completedGoals, timer.goalId],
          categoryStats: newCategoryStats,
          dailyStats: newDailyStats
        };
      }),

      cancelTimer: (timerId) => set((state) => {
        const timer = state.timers.find(t => t.id === timerId);
        if (!timer) return state;

        const updatedTimers = state.timers.map(t =>
          t.id === timerId
            ? { ...t, isCancelled: true, isActive: false, endTime: new Date().toISOString() }
            : t
        );

        // Update category stats
        const newCategoryStats = { ...state.categoryStats };
        if (timer.category && newCategoryStats[timer.category]) {
          newCategoryStats[timer.category].cancelled += 1;
        }

        // Update daily stats
        const today = new Date().toDateString();
        const newDailyStats = { ...state.dailyStats };
        if (!newDailyStats[today]) {
          newDailyStats[today] = { completed: 0, cancelled: 0, xpEarned: 0 };
        }
        newDailyStats[today].cancelled += 1;

        return {
          timers: updatedTimers,
          categoryStats: newCategoryStats,
          dailyStats: newDailyStats
        };
      }),

      updateCategoryStats: (category, action) => set((state) => {
        const newCategoryStats = { ...state.categoryStats };
        if (newCategoryStats[category] && newCategoryStats[category][action] !== undefined) {
          newCategoryStats[category][action] += 1;
        }
        return { categoryStats: newCategoryStats };
      }),

      getActiveTimer: () => {
        const { timers } = get();
        return timers.find(t => t.isActive && !t.isCompleted && !t.isCancelled);
      },

      getStats: () => {
        const { timers, goals, xp, categoryStats, dailyStats } = get();
        const today = new Date().toDateString();
        const todayStats = dailyStats[today] || { completed: 0, cancelled: 0, xpEarned: 0 };

        return {
          totalGoals: goals.length,
          totalTimers: timers.length,
          activeTimers: timers.filter(t => t.isActive && !t.isCompleted && !t.isCancelled).length,
          completedTimers: timers.filter(t => t.isCompleted).length,
          cancelledTimers: timers.filter(t => t.isCancelled).length,
          totalXP: xp,
          todayCompleted: todayStats.completed,
          todayCancelled: todayStats.cancelled,
          todayXP: todayStats.xpEarned,
          categoryStats,
          successRate: timers.length > 0
            ? Math.round((timers.filter(t => t.isCompleted).length / timers.length) * 100)
            : 0
        };
      },

      // Add missing methods
      pauseTimer: (timerId, metadata = {}) => set((state) => {
        const updatedTimers = state.timers.map(t =>
          t.id === timerId
            ? { ...t, isPaused: true, pauseTime: new Date().toISOString(), ...metadata }
            : t
        );
        return { timers: updatedTimers };
      }),

      resumeTimer: (timerId, metadata = {}) => set((state) => {
        const updatedTimers = state.timers.map(t =>
          t.id === timerId
            ? {
              ...t,
              isPaused: false,
              pauseTime: null,
              totalPausedDuration: (t.totalPausedDuration || 0) +
                (t.pauseTime ? (new Date() - new Date(t.pauseTime)) / 1000 : 0),
              ...metadata
            }
            : t
        );
        return { timers: updatedTimers };
      }),

      stopTimer: (timerId, metadata = {}) => set((state) => {
        const timer = state.timers.find(t => t.id === timerId);
        if (!timer) return state;

        const isForceStop = metadata.forceStop || metadata.applyPenalty;
        let xpChange = 0;

        if (isForceStop) {
          // Apply 60% XP penalty
          const potentialXP = timer.xpReward || 0;
          const penalty = Math.round(potentialXP * 0.6);
          xpChange = -penalty;
        } else {
          // Normal completion - award full XP
          xpChange = timer.xpReward || 0;
        }

        const updatedTimers = state.timers.map(t =>
          t.id === timerId
            ? {
              ...t,
              isActive: false,
              isCompleted: !isForceStop,
              isCancelled: isForceStop,
              endTime: new Date().toISOString(),
              ...metadata
            }
            : t
        );

        const newXP = Math.max(0, state.xp + xpChange);

        return {
          timers: updatedTimers,
          xp: newXP
        };
      }),

      completeTask: (xpAmount = 10) => set((state) => {
        const today = new Date().toDateString();
        const lastActive = state.lastActiveDate;

        // Update Streak
        let newStreak = state.streak;
        if (lastActive !== today) {
          const yesterday = new Date();
          yesterday.setDate(yesterday.getDate() - 1);
          if (lastActive === yesterday.toDateString()) {
            newStreak += 1;
          } else {
            newStreak = 1; // Reset if missed a day (or first day)
          }
        }

        // Update Daily Stats
        const newDailyStats = { ...state.dailyStats };
        if (!newDailyStats[today]) {
          newDailyStats[today] = { completed: 0, cancelled: 0, xpEarned: 0 };
        }
        newDailyStats[today].completed += 1;
        newDailyStats[today].xpEarned += xpAmount;

        return {
          xp: state.xp + xpAmount,
          streak: newStreak,
          lastActiveDate: today,
          dailyStats: newDailyStats
        };
      }),

      // Add missing cleanupCompletedTimers method
      cleanupCompletedTimers: () => set((state) => {
        const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000);
        const activeTimers = state.timers.filter(timer => {
          // Keep active timers
          if (timer.isActive && !timer.isCompleted && !timer.isCancelled) {
            return true;
          }

          // Keep recently completed/cancelled timers (within 1 hour)
          if (timer.endTime) {
            const endTime = new Date(timer.endTime);
            return endTime > oneHourAgo;
          }

          return false;
        });

        return { timers: activeTimers };
      }),

      // Add missing utility methods
      deleteGoal: (goalId) => set((state) => ({
        goals: state.goals.filter(goal => goal.id !== goalId),
        timers: state.timers.filter(timer => timer.goalId !== goalId)
      })),

      updateGoal: (goalId, updates) => set((state) => ({
        goals: state.goals.map(goal =>
          goal.id === goalId ? { ...goal, ...updates } : goal
        )
      })),

      resetStats: () => set(() => ({
        xp: 0,
        completedGoals: [],
        streak: 0,
        categoryStats: {
          productivity: { accepted: 0, completed: 0, cancelled: 0 },
          health: { accepted: 0, completed: 0, cancelled: 0 },
          wellness: { accepted: 0, completed: 0, cancelled: 0 },
          learning: { accepted: 0, completed: 0, cancelled: 0 }
        },
        dailyStats: {}
      })),

      // Add timer validation
      canStartTimer: (goalId) => {
        const { timers, goals } = get();
        const activeTimers = timers.filter(t => t.isActive && !t.isCompleted && !t.isCancelled);

        // Check maximum active timers limit
        if (activeTimers.length >= 5) {
          return { canStart: false, reason: 'Maximum 5 active timers allowed' };
        }

        // Check if goal already has an active timer
        const goalHasActiveTimer = activeTimers.some(t => t.goalId === goalId);
        if (goalHasActiveTimer) {
          return { canStart: false, reason: 'Goal already has an active timer' };
        }

        // Check if goal exists
        const goal = goals.find(g => g.id === goalId);
        if (!goal) {
          return { canStart: false, reason: 'Goal not found' };
        }

        return { canStart: true, reason: null };
      },

      // Add method to initialize sample data if empty
      initializeSampleData: () => set((state) => {
        if (state.goals.length === 0) {
          const sampleGoals = [
            {
              id: 'sample-1',
              name: 'Morning Meditation',
              category: 'wellness',
              duration: 600, // 10 minutes
              xpReward: 15,
              difficulty: 'easy'
            },
            {
              id: 'sample-2',
              name: 'Focus Work Session',
              category: 'productivity',
              duration: 1800, // 30 minutes
              xpReward: 25,
              difficulty: 'medium'
            },
            {
              id: 'sample-3',
              name: 'Reading Session',
              category: 'learning',
              duration: 1200, // 20 minutes
              xpReward: 20,
              difficulty: 'easy'
            }
          ];

          return {
            goals: sampleGoals,
            categoryStats: {
              productivity: { accepted: 1, completed: 0, cancelled: 0 },
              health: { accepted: 0, completed: 0, cancelled: 0 },
              wellness: { accepted: 1, completed: 0, cancelled: 0 },
              learning: { accepted: 1, completed: 0, cancelled: 0 }
            }
          };
        }
        return state;
      }),

      // Add method to get active timers
      getActiveTimers: () => {
        const { timers } = get();
        return timers.filter(t => t.isActive && !t.isCompleted && !t.isCancelled);
      }
    }),
    {
      name: 'discipline-storage',
      partialize: (state) => ({
        goals: state.goals,
        timers: state.timers,
        xp: state.xp,
        completedGoals: state.completedGoals,
        streak: state.streak,
        categoryStats: state.categoryStats,
        dailyStats: state.dailyStats,
        dailyTasks: state.dailyTasks,
        routines: state.routines
      })
    }
  )
);

export default useDisciplineStore;
export { useDisciplineStore };
